import * as React from 'react';
import { Link } from 'react-router-dom';

import Button from '@/components/elements/Button/BaseButton';
import { useUserContext } from '@/contexts/user';
import { hasDmmLoginCookieInEvenOnce } from '@/lib/auth';
import { getLoginUrl, getSignUpUrl } from '@/lib/dmm/accounts';
import { OrderStep } from '@/modelTypes';

import { LoginWrapper, VerificationWrapper, Wrapper } from './style';

interface ReadyButtonProps {
  disabled: boolean;
  isAutoChargeByOther: boolean;
  setOrderStep: React.Dispatch<React.SetStateAction<OrderStep>>;
}

const ReadyButton: React.FC<ReadyButtonProps> = ({
  disabled,
  isAutoChargeByOther,
  setOrderStep,
}) => {
  const { isLoggedIn, isWaiting, isSmsWaiting, isGuest } = useUserContext();

  // 未ログイン且つ過去にログインしたことがある場合
  if (!isLoggedIn && hasDmmLoginCookieInEvenOnce()) {
    return (
      <LoginWrapper>
        <p>投票するにはDMM競輪へのログインが必要です</p>
        <a href={getLoginUrl()} data-testid="login-link">
          <Button
            size="m"
            bgColor="primary"
            fontWeight="bold"
            width="280px"
            height="56px"
            radius="l"
          >
            ログイン
          </Button>
        </a>
        <span>
          アカウントをお持ちでない場合は
          <a href={getSignUpUrl()}>会員登録（無料）</a>をしてください。
        </span>
      </LoginWrapper>
    );
  }

  // 未ログイン且つ過去にログインしたことがない場合
  if (!isLoggedIn && !hasDmmLoginCookieInEvenOnce()) {
    return (
      <LoginWrapper>
        <p>投票するにはDMM競輪の会員登録が必要です</p>
        <a href={getSignUpUrl()} data-testid="sign-up-link">
          <Button
            size="m"
            bgColor="primary"
            fontWeight="bold"
            width="280px"
            height="56px"
            radius="l"
          >
            無料会員登録する
          </Button>
        </a>
        <span>
          アカウントをお持ちの場合は
          <a href={getLoginUrl()}>ログイン</a>してください。
        </span>
      </LoginWrapper>
    );
  }

  // ゲスト会員且つ審査中でない場合
  if (isGuest) {
    return (
      <VerificationWrapper>
        <p>投票するにはDMM競輪の利用登録が必要です</p>
        <Link to="/signup/registration" data-testid="registration-link">
          <Button
            size="m"
            bgColor="primary"
            fontWeight="bold"
            width="280px"
            height="56px"
            radius="l"
          >
            利用登録する（無料）
          </Button>
        </Link>
      </VerificationWrapper>
    );
  }

  // SMS認証待ちの場合
  if (isSmsWaiting) {
    return (
      <VerificationWrapper>
        <p>投票するには本人確認が必要です</p>
        <Link to="/signup/verify" data-testid="redirect-link">
          <Button
            size="m"
            bgColor="primary"
            fontWeight="bold"
            width="280px"
            height="56px"
            radius="l"
          >
            本人確認を行う
          </Button>
        </Link>
      </VerificationWrapper>
    );
  }

  // 公的書類提出待ちの場合
  if (isWaiting) {
    return (
      <VerificationWrapper>
        <p>投票するには本人確認が必要です</p>
        <Link to="/signup/redirect" data-testid="redirect-link">
          <Button
            size="m"
            bgColor="primary"
            fontWeight="bold"
            width="280px"
            height="56px"
            radius="l"
          >
            本人確認を行う
          </Button>
        </Link>
      </VerificationWrapper>
    );
  }

  if (isAutoChargeByOther) {
    return (
      <Wrapper>
        <Button
          size="m"
          bgColor="primary"
          disabled={disabled}
          fontWeight="bold"
          width="280px"
          height="56px"
          radius="l"
          onClick={() => setOrderStep(OrderStep.OtherCharge)}
        >
          チャージ方法選択へ
        </Button>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Button
        size="m"
        bgColor="primary"
        disabled={disabled}
        fontWeight="bold"
        width="280px"
        height="56px"
        radius="l"
        onClick={() => setOrderStep(OrderStep.Confirm)}
      >
        投票確認へ
      </Button>
    </Wrapper>
  );
};

export default ReadyButton;
